import data from './timezone-data';

type TranslateFn = (key: string, opts?: { defaultValue: string }) => string;

/** O(1) lookup of IANA id → English label, built once at module load. */
const labelByValue = new Map(data.map((tz) => [tz.value, tz.label]));

/**
 * Helper function to get the (localized) timezone label. Defaults to the timezone if not found.
 *
 * The display label is routed through i18n: each timezone has a `common:timezone.<IANA value>`
 * key. The IANA `value` and search `keywords` on the data entry stay English so timezone
 * resolution and search continue to work; only the human-readable label is localized.
 *
 * Callers pass the `t` function from `useTranslation()` or `i18n.t` so that this module
 * does NOT statically import `@modernloop/i18n` (which would pull ~350 locale JSON files
 * into the datetime barrel's module graph, causing vitest to hang in app-client).
 *
 * @param timezone - timezone value (IANA id)
 * @param t - i18next translate function; when omitted the raw English label is returned.
 * @returns localized timezone label
 */
export const getTimezoneLabel = (timezone: string, t?: TranslateFn) => {
  const label = labelByValue.get(timezone);
  if (!label) {
    return timezone;
  }
  if (!t) {
    return label;
  }
  return t(`common:timezone.${timezone}`, { defaultValue: label });
};
